// @ts-expect-error: @rails/request.js is not typed
import { FetchRequest } from '@rails/request.js'
import { useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'

import { useWindowLocation } from 'utils/windowLocation'

import { type Partner, type PartnerAdmin } from './types'

type AgencyDetails = {
  display_name: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  email: string;
  npn: string;
  california_producer_license_number: string;
}

export default function AgencyDetailForm ({
  partner,
  partnerAdmin,
  isInitialSetup = false,
  showReferralPageInfo = true,
  showOrganizationSettings = true,
}: {
  partner: Partner;
  partnerAdmin: PartnerAdmin;
  isInitialSetup?: boolean;
  showReferralPageInfo?: boolean;
  showOrganizationSettings?: boolean;
}) {
  const { assign } = useWindowLocation()

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AgencyDetails>({
    defaultValues: {
      first_name: partnerAdmin.first_name,
      last_name: partnerAdmin.last_name,
      display_name: partner.display_name,
      phone_number: partner.phone_number,
      email: partner.email,
      npn: partnerAdmin.npn,
      california_producer_license_number:
        partnerAdmin.california_producer_license_number,
    },
  })
  const [submissionError, setSubmissionError] = useState<string | undefined>()

  const formValues = watch()
  const hasChanged = Object.keys(formValues).some((key) => {
    if (['display_name', 'phone_number', 'email'].includes(key)) {
      return (
        formValues[key as keyof AgencyDetails] !==
        partner[key as keyof Partner]
      )
    } else if (
      [
        'first_name',
        'last_name',
        'npn',
        'california_producer_license_number',
      ].includes(key)
    ) {
      return (
        formValues[key as keyof AgencyDetails] !==
        partnerAdmin[key as keyof PartnerAdmin]
      )
    }
    return false
  })

  const onSubmit: SubmitHandler<AgencyDetails> = async (data) => {
    try {
      const payload = {
        partner_admin: {
          first_name: data.first_name,
          last_name: data.last_name,
          npn: data.npn,
          california_producer_license_number:
            data.california_producer_license_number,
        },
        is_initial_setup: isInitialSetup,
      } as any

      if (showOrganizationSettings) {
        payload.partner = {
          display_name: data.display_name,
          phone_number: data.phone_number,
          email: data.email,
        }
      }

      // TODO: this method of making an API call is deprecated -- please use reactquery instead
      // see https://www.notion.so/thatchhealth/Using-React-Query-for-Fetching-Data-160114874bc280379918d43442405589
      const request = new FetchRequest(
        'put',
        '/api/broker/settings/agency_details',
        {
          body: payload,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      )
      const response = await request.perform()
      if (response.ok) {
        const data = await response.json
        assign(data.redirect_to)
      } else if (response.statusCode === 401) {
        setSubmissionError(
          'Your session expired. Please refresh the page and sign in again to continue.'
        )
      } else if (response.statusCode === 422) {
        const errorData = await response.json
        const errors = errorData.errors
        const partnerAdminErrors = errors.partner_admin || []
        const partnerErrors = errors.partner || []
        const finalErrors = partnerAdminErrors
          .concat(partnerErrors)
          .join(', ')
        setSubmissionError(
          `There was an error updating organization details. Please correct the errors and try again: ${finalErrors}`
        )
      } else {
        const errorData = await response.json
        const errors = errorData.errors
        console.error('Error updating organization details: ', errors)
        setSubmissionError(
          'There was an error updating organization details. Please contact support.'
        )
      }
    } catch (error: any) {
      console.error('Error updating organization details: ', error)
      setSubmissionError(
        'There was an error updating organization details. Please contact support.'
      )
    }
  }

  return (
    <>
      {submissionError && (
        <div
          className='bg-red-100 border border-red-400 text-red-700 p-2 mb-4 rounded relative text-center text-xs'
          role='alert'
        >
          {submissionError}
        </div>
      )}
      <form
        className='flex flex-col justify-between gap-y-6 mx-auto'
        onSubmit={handleSubmit(onSubmit)}
      >
        {showReferralPageInfo && (
          <p className='text-sm text-gray-700 rounded-md border border-gray-300 bg-gray-200 p-4'>
            We will display the information below on your referral page to help clients contact you directly.
          </p>
        )}
        {showOrganizationSettings && (
          <div className='flex gap-4 justify-between'>
            <div
              className={`flex flex-col w-full${
              errors.display_name ? ' field-with-errors' : ''
            }`}
            >
              <label className='label-default mb-2' htmlFor='display_name'>
                Organization name
              </label>
              <input
                id='display_name'
                className='input-default'
                aria-invalid={errors.display_name ? 'true' : 'false'}
                {...register('display_name', {
                  required: 'Organization name is required.',
                })}
                autoComplete='organization'
              />
              {errors.display_name && (
                <span className='field-error'>{errors.display_name.message}</span>
              )}
            </div>
            <div className='flex flex-col w-full' />
          </div>
        )}
        <div className='flex gap-4 justify-between'>
          <div
            className={`flex flex-col w-full${
              errors.first_name ? ' field-with-errors' : ''
            }`}
          >
            <label className='label-default mb-2' htmlFor='first_name'>
              First name
            </label>
            <input
              id='first_name'
              className='input-default'
              aria-invalid={errors.first_name ? 'true' : 'false'}
              {...register('first_name', {
                required: 'First name is required.',
              })}
              autoComplete='given-name'
            />
            {errors.first_name && (
              <span className='field-error'>{errors.first_name.message}</span>
            )}
          </div>

          <div
            className={`flex flex-col w-full${
              errors.last_name ? ' field-with-errors' : ''
            }`}
          >
            <label className='label-default mb-2' htmlFor='last_name'>
              Last name
            </label>
            <input
              id='last_name'
              className='input-default'
              aria-invalid={errors.last_name ? 'true' : 'false'}
              {...register('last_name', { required: 'Last name is required.' })}
              autoComplete='family-name'
            />
            {errors.last_name && (
              <span className='field-error'>{errors.last_name.message}</span>
            )}
          </div>
        </div>

        <div className='flex gap-4 justify-between'>
          <div className='flex flex-col w-full'>
            <label className='label-default mb-2' htmlFor='npn'>
              National producer number (NPN)
            </label>
            <input
              id='npn'
              className='input-default'
              {...register('npn')}
              autoComplete='off'
            />
          </div>

          <div className='flex flex-col w-full'>
            <label
              className='label-default mb-2'
              htmlFor='california_producer_license'
            >
              California producer license number
            </label>
            <input
              id='california_producer_license'
              className='input-default'
              {...register('california_producer_license_number')}
              autoComplete='off'
            />
          </div>
        </div>

        {showOrganizationSettings && (
          <div className='flex gap-4 justify-between'>
            <div className='flex flex-col w-full'>
              <label className='label-default mb-2' htmlFor='phone_number'>
                Business phone number
              </label>
              <input
                id='phone_number'
                className='input-default'
                {...register('phone_number')}
                autoComplete='tel'
              />
            </div>

            <div className='flex flex-col w-full'>
              <label className='label-default mb-2' htmlFor='email'>
                Business email
              </label>
              <input
                id='email'
                className='input-default'
                {...register('email')}
                autoComplete='off'
              />
            </div>
          </div>
        )}
        <div className='flex justify-end'>
          <button
            className='button w-auto'
            disabled={!hasChanged}
            type='submit'
          >
            Save
          </button>
        </div>
      </form>
    </>
  )
}
